import { motion, useAnimationFrame, useMotionValue, useTransform } from "motion/react";
import { useRef } from "react";

interface ShinyTextProps {
    text: string;
    disabled?: boolean;
    speed?: number;
    color?: string;
    shineColor?: string;
    spread?: number;
    delay?: number;
}

const ShinyText: React.FC<ShinyTextProps> = ({ text, disabled = false, speed = 2, color = "#b5b5b5", shineColor = "#ffffff", spread = 120, delay = 0 }) => {
    const elapsedRef = useRef(0);
    const lastTimeRef = useRef<number | null>(null);
    const progress = useMotionValue(0);

    const animationDuration = speed * 1000;
    const delayDuration = delay * 1000;

    useAnimationFrame(time => {
        if (disabled) {
            lastTimeRef.current = null;
            return;
        }

        if (lastTimeRef.current === null) {
            lastTimeRef.current = time;
            return;
        }

        const deltaTime = time - lastTimeRef.current;
        lastTimeRef.current = time;

        elapsedRef.current += deltaTime;

        const cycleDuration = animationDuration + delayDuration;
        const cycleTime = elapsedRef.current % cycleDuration;

        if (cycleTime < animationDuration) {
            // Animation phase: 0 -> 100
            const p = (cycleTime / animationDuration) * 100;
            progress.set(p);
        } else {
            // Delay phase - hold at end (shine off-screen)
            progress.set(100);
        }
    });

    const backgroundPosition = useTransform(progress, p => `${150 - p * 2}% center`);

    const gradientStyle: React.CSSProperties = {
        backgroundImage: `linear-gradient(${spread}deg, ${color} 0%, ${color} 35%, ${shineColor} 50%, ${color} 65%, ${color} 100%)`,
        backgroundSize: "200% auto",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent"
    };

    return (
        <motion.span
            data-text={text}
            style={{
                ...gradientStyle,
                backgroundPosition,
                lineHeight: 1.2,
                paddingBlock: "0.08rem",
                display: "inline-block",
                position: "relative",
                fontSize: "1rem",
                fontWeight: 600,
                letterSpacing: 0.3,
                color: color // base text is ALWAYS visible
            }}
        >
            {text}
        </motion.span>
    );
};

export default ShinyText;
